// Copyright (c) HashiCorp, Inc
// SPDX-License-Identifier: MPL-2.0
import chalk from "chalk";
import * as fs from "fs-extra";
import { convert as hcl2cdkConvert } from "@cdktn/hcl2cdk";
import {
  GetOptions,
  TerraformModuleConstraint,
  TerraformProviderConstraint,
} from "@cdktn/provider-generator";

import {
  LANGUAGES,
  Language,
  readConfigSync,
  sendTelemetry,
  Errors,
  IsErrorType,
  logger,
  collectDebugInformation,
  getPackageVersion,
  TerraformDependencyConstraint,
  ConstructsMakerProviderTarget,
} from "@cdktn/commons";

import { checkForEmptyDirectory, runInit } from "./helper/init";
import { terraformCheck } from "./helper/terraform-check";
import * as terraformCloudClient from "./helper/terraform-cloud-client";
import { TerraformLogin } from "./helper/terraform-login";
import { readStreamAsString } from "./helper/utilities";
import { displayVersionMessage } from "./helper/version-check";

import { runDiff } from "./ui/diff";
import { runDeploy } from "./ui/deploy";
import { runDestroy } from "./ui/destroy";
import { runGet } from "./ui/get";
import { runList } from "./ui/list";
import { runSynth } from "./ui/synth";
import { runWatch } from "./ui/watch";
import { printProviderList } from "./ui/provider-list";

import {
  NestedTerraformOutputs,
  saveOutputs,
  normalizeOutputPath,
  initializErrorReporting,
  DependencyManager,
  ProviderConstraint,
  CdktfConfig,
  get as getLib,
  providerAdd as providerAddLib,
} from "@cdktn/cli-core";
import { runOutput } from "./ui/output";
import { throwIfNotProjectDirectory } from "./helper/check-directory";
import {
  checkEnvironment,
  verifySimilarLibraryVersion,
} from "./helper/check-environment";
import { sanitizeVarFiles } from "./helper/var-files";
import { askForCrashReportingConsent } from "./helper/error-reporting";
import { startPerformanceMonitoring } from "./helper/performance";
import path from "path";
import os from "os";
import { readPackageJson, projectRootPath } from "./helper/utilities";
import { readSchema } from "@cdktn/provider-schema";
import { editor } from "@inquirer/prompts";

const chalkColour = new chalk.Instance();
const config = readConfigSync();

async function getProviderRequirements(provider: string[]) {
  let providersFromConfig: (string | TerraformDependencyConstraint)[] = [];

  try {
    const config = CdktfConfig.read();
    providersFromConfig = config.terraformProviders;
  } catch (e) {
    if (IsErrorType(e, "External")) {
      // do nothing, expected if run in a different directory
    } else {
      throw e;
    }
  }
  return [...provider, ...providersFromConfig];
}

export async function convert({
  language,
  provider,
  stack,
  experimentalProviderSchemaCachePath,
}: any) {
  await initializErrorReporting();
  await displayVersionMessage();

  const pkg = readPackageJson();

  const providerRequirements = await getProviderRequirements(provider);

  // Get all the provider schemas
  const { providerSchema } = await readSchema(
    providerRequirements.map((spec) =>
      ConstructsMakerProviderTarget.from(
        new TerraformProviderConstraint(spec),
        LANGUAGES[0],
      ),
    ),
    experimentalProviderSchemaCachePath,
  );

  let input: string | undefined = undefined;
  try {
    input = await readStreamAsString(process.stdin);
  } catch {
    logger.debug(`No TTY stream passed to convert, using interactive input`);
    try {
      input = await editor({
        message:
          "Enter your Terrafrom code to convert to cdktn (or run the command again and pass it as stdin):",
        postfix: ".tf",
        waitForUserInput: true,
      });
    } catch {
      throw Errors.Usage(
        "No Terraform code to convert was provided. Please provide Terraform code to convert as stdin or run the command again and let the CLI open the editor.",
      );
    }
  }

  const needsProject = language !== "typescript";

  const origDir = process.cwd();
  if (needsProject) {
    const tempDir = await fs.mkdtemp(path.join(os.tmpdir(), "cdktf-convert-"));
    process.chdir(tempDir);

    // Support for local development vs published version
    const projectRoot = projectRootPath();
    const dist = path.resolve(projectRoot, "../../dist");

    logger.setLevel("ERROR");
    await init({
      template: "typescript",
      providers: provider || [],
      projectName: path.basename(tempDir),
      projectDescription: "Temporary project for conversion",
      local: true,
      enableCrashReporting: false,
      fromTerraformProject: "no",
      dist: pkg.version === "0.0.0" ? dist : undefined,
      cdktfVersion: pkg.version,
      silent: true,
      nonInteractive: true,
    });
    logger.useDefaultLevel();
  }

  let output;
  try {
    const { all, stats } = await hcl2cdkConvert(input, {
      language,
      providerSchema: providerSchema ?? {},
      codeContainer: stack ? "cdktn.TerraformStack" : "constructs.Construct",
    });
    output = all;

    await sendTelemetry("convert", { ...stats, error: false });
  } catch (err: any) {
    throw Errors.Internal((err as Error).message, err, { language });
  }

  if (needsProject) {
    process.chdir(origDir);
  }

  console.log(output);
}

export async function deploy(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  await checkEnvironment();
  const command = argv.app;
  const outDir = argv.output;
  const autoApprove = argv.autoApprove;
  const stacks = argv.stacks;
  const includeSensitiveOutputs = argv.outputsFileIncludeSensitiveOutputs;
  const refreshOnly = argv.refreshOnly;
  const terraformParallelism = argv.terraformParallelism;
  const ignoreMissingStackDependencies =
    argv.ignoreMissingStackDependencies || false;
  const parallelism = argv.parallelism;
  const vars = argv.var;
  const varFiles = sanitizeVarFiles(argv.varFile);
  const noColor = argv.noColor;
  const migrateState = argv.migrateState;
  const skipSynth = argv.skipSynth;
  const skipProviderLock = argv.skipProviderLock;

  let outputsPath: string | undefined = undefined;

  let onOutputsRetrieved: (outputs: NestedTerraformOutputs) => void = () => {};

  if (argv.outputsFile) {
    outputsPath = normalizeOutputPath(argv.outputsFile);
    onOutputsRetrieved = (outputs: NestedTerraformOutputs) =>
      saveOutputs(outputsPath!, outputs, includeSensitiveOutputs);
  }
  await terraformCheck();
  await runDeploy({
    outDir,
    targetStacks: stacks,
    synthCommand: command,
    autoApprove,
    onOutputsRetrieved,
    outputsPath,
    ignoreMissingStackDependencies,
    parallelism,
    refreshOnly,
    terraformParallelism,
    vars,
    varFiles,
    noColor,
    migrateState,
    skipSynth,
    skipProviderLock,
  });
}

export async function destroy(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  await checkEnvironment();
  const command = argv.app;
  const outDir = argv.output;
  const autoApprove = argv.autoApprove;
  const stacks = argv.stacks;
  const ignoreMissingStackDependencies =
    argv.ignoreMissingStackDependencies || false;
  const parallelism = argv.parallelism;
  const terraformParallelism = argv.terraformParallelism;
  const vars = argv.var;
  const varFiles = sanitizeVarFiles(argv.varFile);
  const noColor = argv.noColor;
  const migrateState = argv.migrateState;
  const skipSynth = argv.skipSynth;
  const skipProviderLock = argv.skipProviderLock;

  await terraformCheck();
  await runDestroy({
    outDir,
    targetStacks: stacks,
    synthCommand: command,
    autoApprove,
    ignoreMissingStackDependencies,
    parallelism,
    terraformParallelism,
    vars,
    varFiles,
    noColor,
    migrateState,
    skipSynth,
    skipProviderLock,
  });
}

export async function diff(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  await checkEnvironment();
  const command = argv.app;
  const outDir = argv.output;
  const stack = argv.stack;
  const refreshOnly = argv.refreshOnly;
  const terraformParallelism = argv.terraformParallelism;
  const vars = argv.var;
  const varFiles = sanitizeVarFiles(argv.varFile);
  const noColor = argv.noColor;
  const migrateState = argv.migrateState;
  const skipSynth = argv.skipSynth;
  const skipProviderLock = argv.skipProviderLock;

  await terraformCheck();
  await runDiff({
    outDir,
    refreshOnly,
    targetStack: stack,
    synthCommand: command,
    terraformParallelism,
    vars,
    varFiles,
    noColor,
    migrateState,
    skipSynth,
    skipProviderLock,
  });
}

export async function get(argv: {
  output: string;
  language: Language;
  parallelism: number;
  force?: boolean;
  showPerformanceInfo?: boolean;
  silent?: boolean;
  experimentalProviderSchemaCachePath?: string;
}) {
  const printPerformanceInfo = argv.showPerformanceInfo
    ? startPerformanceMonitoring()
    : () => {};

  try {
    throwIfNotProjectDirectory();
    await displayVersionMessage();
    await initializErrorReporting(askForCrashReportingConsent);
    await checkEnvironment();
    await verifySimilarLibraryVersion();
    const config = readConfigSync(); // read config again to be up-to-date (if called via 'add' command)
    const providers = config.terraformProviders ?? [];
    const modules = config.terraformModules ?? [];
    const { output, language, parallelism, force } = argv;

    const constraints: TerraformDependencyConstraint[] = [
      ...providers.map((c) => new TerraformProviderConstraint(c)),
      ...modules.map((c) => new TerraformModuleConstraint(c)),
    ];

    if (constraints.length === 0) {
      logger.warn(
        `WARNING: No providers or modules found in "cdktf.json" config file, therefore cdktn get does nothing.`,
      );
      return;
    }

    await terraformCheck();
    await runGet({
      codeMakerOutput: output,
      language: language,
      constraints,
      parallelism,
      force,
      silent: argv.silent,
      providerSchemaCachePath: argv.experimentalProviderSchemaCachePath,
      languageOptions: config.languageOptions,
      targetVersions: config.targetVersions,
    });
  } finally {
    if (!argv.silent) {
      printPerformanceInfo();
    }
  }
}

export async function init(argv: any) {
  await terraformCheck();
  await displayVersionMessage();
  await checkEnvironment();

  if (["", ".", process.cwd()].includes(argv.fromTerraformProject)) {
    throw Errors.Usage(
      "--from-terraform-project requires a path to an existing Terraform project to be set, e.g. --from-terraform-project=../my-tf-codebase This folder can not be the same as the current working directory since cdktn init will initialize the new project in that folder.",
    );
  }

  checkForEmptyDirectory(".");

  const { needsGet, codeMakerOutput, language } = await runInit(argv);

  if (needsGet) {
    if (!argv.silent) {
      console.log(
        "Local providers have been updated. Running cdktn get to update...",
      );
    }
    await get({
      language,
      output: codeMakerOutput,
      parallelism: 1,
      silent: argv.silent,
    });
  }

  if (language === Language.GO) {
    console.log(
      "Run 'go mod tidy' after adding imports for any needed modules such as prebuilt providers",
    );
  }
}

export async function list(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  await checkEnvironment();
  const command = argv.app;
  const outDir = argv.output;

  await terraformCheck();
  await runList({ outDir, synthCommand: command });
}

export async function login(argv: { tfeHostname: string }) {
  await terraformCheck();
  await displayVersionMessage();

  async function showUserDetails(authToken: string) {
    // Get user details if token is set
    const userAccount = await terraformCloudClient.getAccountDetails(
      argv.tfeHostname,
      authToken,
    );
    if (userAccount) {
      const username = userAccount.data.attributes.username;
      console.log(
        chalkColour`\n{greenBright cdktn has successfully configured Terraform Cloud credentials!}`,
      );
      console.log(chalkColour`\nWelcome {bold ${username}}!`);
    } else {
      throw Errors.Usage(`Configured Terraform Cloud token is invalid.`);
    }
  }

  const terraformLogin = new TerraformLogin(argv.tfeHostname);
  let token = "";
  try {
    token = await readStreamAsString(process.stdin);
    token = token.replace(/\n/g, "");
  } catch {
    logger.debug(`No TTY stream passed to login`);
  }

  // If we get a token through stdin, we don't need to ask for credentials, we just validate and set it
  // This is useful for programmatically authenticating, e.g. a CI server
  if (!token) {
    token = await terraformLogin.askToLogin(false);
    if (token === "") {
      throw Errors.Usage(`No Terraform Cloud token was provided.`);
    }
  }

  await terraformLogin.saveTerraformCredentials(token);
  await showUserDetails(token);
}

export async function synth(argv: any) {
  const printPerformanceInfo = argv.showPerformanceInfo
    ? startPerformanceMonitoring()
    : () => {};

  try {
    await initializErrorReporting(askForCrashReportingConsent);
    throwIfNotProjectDirectory();
    await displayVersionMessage();
    await checkEnvironment();
    const checkCodeMakerOutput = argv.checkCodeMakerOutput;
    const command = argv.app;
    const outDir = argv.output;
    const hcl = argv.hcl;

    if (
      checkCodeMakerOutput &&
      !(await fs.pathExists(config.codeMakerOutput))
    ) {
      console.error(
        `ERROR: synthesis failed, run "cdktn get" to generate providers in ${config.codeMakerOutput}`,
      );
      process.exit(1);
    }

    await terraformCheck();
    await runSynth({
      outDir,
      synthCommand: command,
      hcl,
    });
  } finally {
    printPerformanceInfo();
  }
}

export async function watch(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  const command = argv.app;
  const outDir = argv.output;
  const autoApprove = argv.autoApprove;
  const stacks = argv.stacks;
  const terraformParallelism = argv.terraformParallelism;
  const parallelism = argv.parallelism;

  if (!autoApprove) {
    console.error(
      chalkColour`{redBright ERROR: The watch command always automatically deploys and approves changes. To make this behaviour explicit the --auto-approve flag must be set}`,
    );
    process.exit(1);
  }

  await terraformCheck();
  await runWatch({
    targetDir: outDir,
    targetStacks: stacks,
    synthCommand: command,
    autoApprove,
    terraformParallelism,
    parallelism,
  });
}

export async function output(argv: any) {
  await initializErrorReporting(askForCrashReportingConsent);
  throwIfNotProjectDirectory();
  await displayVersionMessage();
  await checkEnvironment();
  const command = argv.app;
  const outDir = argv.output;
  const stacks = argv.stacks;
  const includeSensitiveOutputs = argv.outputsFileIncludeSensitiveOutputs;
  const skipSynth = argv.skipSynth;
  const skipProviderLock = argv.skipProviderLock;
  let outputsPath: string | undefined = undefined;

  let onOutputsRetrieved: (outputs: NestedTerraformOutputs) => void = () => {};

  if (argv.outputsFile) {
    outputsPath = normalizeOutputPath(argv.outputsFile);
    onOutputsRetrieved = (outputs: NestedTerraformOutputs) =>
      saveOutputs(outputsPath!, outputs, includeSensitiveOutputs);
  }

  await terraformCheck();
  await runOutput({
    outDir,
    targetStacks: stacks,
    synthCommand: command,
    onOutputsRetrieved,
    outputsPath,
    skipSynth,
    skipProviderLock,
  });
}

export async function debug(argv: any) {
  // Ideally the provider info gathering happens in `collectDebugInformation()` but the inclusion of `CdktfConfig.read()` in @cdktn/commons causes the build to fail with "error TS5055"
  const config = CdktfConfig.read();
  const language = config.language;
  const cdktnVersion = await getPackageVersion(language, "cdktn");
  const cdktfVersion = await getPackageVersion(language, "cdktf");

  if (!cdktnVersion && !cdktfVersion)
    throw Errors.External(
      "Could not determine cdktf/cdktn version. Please make sure you are in a directory containing a cdktn project and have all dependencies installed.",
    );

  const manager = new DependencyManager(
    language,
    cdktfVersion ?? cdktnVersion!,
    config.projectDirectory,
  );
  const allProviders = await manager.allProviders();
  const debugOutput = await collectDebugInformation();
  if (argv.json) {
    console.log(
      JSON.stringify(
        {
          ...debugOutput,
          providers: {
            local: allProviders.local.map((provider) => {
              return {
                provider: `${provider.providerName}@${provider.providerConstraint}`,
                terraformProviderVersion: provider.providerVersion,
              };
            }),
            prebuilt: allProviders.prebuilt.map((provider) => {
              return {
                provider: provider.packageName,
                terraformProviderVersion: provider.providerVersion,
                prebuiltProviderVersion: provider.packageVersion,
                cdktfVersion: provider.cdktfVersion,
                cdktnVersion: provider.cdktnVersion,
              };
            }),
          },
        },
        null,
        2,
      ),
    );
  } else {
    console.log(chalkColour`{bold {greenBright cdktn debug}}`);

    Object.entries(debugOutput).forEach(([key, value]) => {
      console.log(`${key}: ${value === null ? "null" : value}`);
    });

    console.log(chalkColour`{bold {yellowBright providers}}`);

    for (const provider of allProviders.local) {
      console.log(
        `${provider.providerName}@${provider.providerConstraint} (LOCAL)
        terraform provider version: ${provider.providerVersion}`,
      );
    }
    for (const provider of allProviders.prebuilt) {
      console.log(
        `${provider.packageName} (PREBUILT)
        terraform provider version: ${provider.providerVersion} 
        prebuilt provider version: ${provider.packageVersion}
        cdktf version: ${provider.cdktfVersion}
        cdktn version: ${provider.cdktnVersion}`,
      );
    }
  }
}

export async function providerAdd(argv: any) {
  const config = CdktfConfig.read();
  const language = config.language;

  const cdktnVersion = await getPackageVersion(language, "cdktn");
  const cdktfVersion = await getPackageVersion(language, "cdktf");

  if (!cdktnVersion && !cdktfVersion)
    throw Errors.External(
      "Could not determine cdktf/cdktn version. Please make sure you are in a directory containing a cdktn project and have all dependencies installed.",
    );

  const needsGet = await providerAddLib({
    providers: argv.provider,
    language: language,
    projectDirectory: config.projectDirectory,
    cdktfVersion: cdktfVersion ?? cdktnVersion,
    forceLocal: argv.forceLocal,
  });

  if (needsGet) {
    console.log(
      "Local providers have been updated. Running cdktn get to update...",
    );
    await get({
      language: language,
      output: config.codeMakerOutput,
      parallelism: 1,
      silent: argv.silent,
    });
  }

  if (language === Language.GO) {
    console.log(
      "After adding this module to your imports, please run 'go mod tidy' to resolve newly added modules",
    );
  }
}

export async function providerUpgrade(argv: any) {
  const config = CdktfConfig.read();

  const language = config.language;

  const cdktnVersion = await getPackageVersion(language, "cdktn");
  const cdktfVersion = await getPackageVersion(language, "cdktf");

  if (!cdktnVersion && !cdktfVersion)
    throw Errors.External(
      "Could not determine CDKTF/CDKTN version. Please make sure you are in a directory containing a CDKTN project and have all dependencies installed.",
    );

  const manager = new DependencyManager(
    language,
    cdktfVersion ?? cdktnVersion!,
    config.projectDirectory,
  );

  const constraintsToUpdate: ProviderConstraint[] = [];

  for (const provider of argv.provider) {
    const constraint = ProviderConstraint.fromConfigEntry(provider);
    const { addedLocalProvider } = await manager.upgradeProvider(constraint);
    if (addedLocalProvider) {
      constraintsToUpdate.push(constraint);
    }
  }

  if (constraintsToUpdate.length > 0) {
    const singular = constraintsToUpdate.length === 1;
    console.log(
      `${constraintsToUpdate.length} local provider${
        singular ? " has" : "s have"
      } been updated. Running cdktn get to update...`,
    );

    const config = readConfigSync(); // read config again to be up-to-date (if called via 'add' command)
    const providers = config.terraformProviders ?? [];
    const modules = config.terraformModules ?? [];

    const constructsOptions: GetOptions = {
      codeMakerOutput: config.codeMakerOutput,
      targetLanguage: language,
      languageOptions: config.languageOptions,
      jsiiParallelism: 1,
      targetVersions: config.targetVersions,
    };

    const constraints: TerraformDependencyConstraint[] = [
      ...providers,
      ...modules,
    ];
    await getLib({
      constructsOptions,
      constraints,
      cleanDirectory: false,
      constraintsToGenerate: constraintsToUpdate.map(
        (c) => new TerraformProviderConstraint(c),
      ),
    });
  }

  if (language === Language.GO) {
    console.log(
      "Update your imports to reflect this modules upgrade, then please run 'go mod tidy' to resolve newly added modules",
    );
  }
}

export async function providerList(argv: any) {
  const config = CdktfConfig.read();
  const language = config.language;

  const cdktnVersion = await getPackageVersion(language, "cdktn");
  const cdktfVersion = await getPackageVersion(language, "cdktf");

  if (!cdktnVersion && !cdktfVersion)
    throw Errors.External(
      "Could not determine cdktf/cdktn version. Please make sure you are in a directory containing a cdktn project and have all dependencies installed.",
    );

  const manager = new DependencyManager(
    language,
    cdktfVersion ?? cdktnVersion!,
    config.projectDirectory,
  );
  const allProviders = await manager.allProviders();
  if (argv.json) {
    console.log(JSON.stringify(allProviders));
    return;
  }
  const data = [];
  for (const provider of allProviders.local) {
    data.push({
      "Provider Name": provider.providerName || "",
      "Provider Version": provider.providerVersion || "",
      CDKTF: "",
      CDKTN: "",
      Constraint: provider.providerConstraint || "",
      "Package Name": "",
      "Package Version": "",
    });
  }
  for (const provider of allProviders.prebuilt) {
    data.push({
      "Provider Name": provider.providerName || "",
      "Provider Version": provider.providerVersion || "",
      CDKTF: provider.cdktfVersion || "",
      CDKTN: provider.cdktnVersion || "",
      Constraint: "",
      "Package Name": provider.packageName || "",
      "Package Version": provider.packageVersion || "",
    });
  }
  await terraformCheck();
  printProviderList(data);
}
